import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Table from '../../ui/Table';

import CreateProjectModal from './CreateProjectModal';
import Menus from '../../ui/Menus';
import { useEffect, useState } from 'react';

import { getProjects, getProjectsCount } from '../../apis/projects/projects';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import UpdateIcon from '../../assets/icons/UpdateIcon';
import DeleteProjectModal from './DeleteProjectModal';
import { useLoadingState } from '../../hooks/useLoader';
import Loader from '../../ui/Loader';
import NoDataAvailable from '../../ui/NoDataAvailable';
import AvatarImage from '../../ui/AvatarImage';
import TruncatedTextWithTooltip from '../../ui/TruncatedTextWithTooltip';
import Pagination from '../../ui/Pagination';

function ProjectsList() {
    const navigate = useNavigate();
    const { loading, startLoading, stopLoading } = useLoadingState();
    const [isModalOpen, setModalOpen] = useState(false); // Modal visibility state
    const [modalAction, setModalAction] = useState(''); // Track action type: 'duplicate' or 'delete'
    const [selectedProject, setSelectedProject] = useState(null); // Track selected bounty for modal
    const { id } = useParams();
    const location = useLocation();
    let relativeAddress = location.pathname.split('/');
    relativeAddress.pop();
    relativeAddress = relativeAddress.join('/');
    const [projects, setProjects] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalEntries, setTotalEntries] = useState(0);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    useEffect(
        function () {
            const fetchData = async () => {
                try {
                    startLoading();
                    const countData = await getProjectsCount(id);
                    setTotalEntries(countData?.entryCount || 0);
                    const data = await getProjects(id, currentPage, entriesPerPage);

                    setProjects(data);
                    stopLoading();
                } catch (error) {
                    console.log(error);
                }
            };
            fetchData();
        },
        [id, currentPage, entriesPerPage]
    );

    const entriesHandler = (val) => {
        setEntriesPerPage(val);
        setCurrentPage(1);
    };

    const handleOpenModal = (action, project) => {
        setModalAction(action); // Set the action type ('duplicate' or 'delete')
        setSelectedProject(project); // Set the selected bounty
        setModalOpen(true); // Open the modal
    };

    const handleCloseModal = () => {
        setModalOpen(false); // Close the modal
        setModalAction(''); // Clear action type
        setSelectedProject(null); // Clear selected bounty
    };

    const handleConfirmAction = (id, data) => {
        if (modalAction === 'update') {
            const afterUpdation = projects.map((project) => {
                if (project._id === id) return { ...project, ...data };
                return project;
            });

            setProjects(afterUpdation);

            // Perform duplication logic here
        } else if (modalAction === 'delete') {
            const afterDelete = projects.filter((project) => project._id !== id);
            setProjects(afterDelete);
        }
        handleCloseModal();
    };

    return (
        <>
            <Menus>
                <Table className="items-center" cols="grid-cols-tableProjects gap-14">
                    <Table.Description
                        descriptionClass=" !mb-0"
                        description="Projects in the Organisation "
                        primaryBtnBlueModal="+ Add Project"
                        modalNeedToOpen={
                            <CreateProjectModal
                                startCreating={startLoading}
                                stopCreating={stopLoading}
                                setProjects={setProjects}
                            />
                        }
                    />

                    <p className="mb-4 text-textSecondary ">Click on a project to add new bounties or make modifications to the project</p>

                    <Table.Header>
                        <p className="text-sm   font-semibold text-textSecondary ">Project ID</p>
                        <p className="text-sm font-semibold text-textSecondary ">Project Title</p>
                        <p className="text-sm   font-semibold text-textSecondary ">Bounties Live</p>
                        <p className="text-sm   font-semibold text-textSecondary ">Created By</p>
                        <p className="text-sm  font-semibold text-textSecondary "></p>
                    </Table.Header>
                    {!loading ? (
                        projects.length > 0 ? (
                            projects.map((project) => (
                                <Table.Row key={project._id} rowClassName="relative">
                                    <TruncatedTextWithTooltip
                                        onClick={() => navigate(project._id)}
                                        containerClassName={'self-center'}
                                        textClassName="text-textBrandBlue   cursor-pointer truncate w-full uppercase font-medium"
                                        text={project._id}
                                    />

                                    <p className="text-textPrimary">{project.title}</p>
                                    <p className="text-textPrimary">
                                        {project.liveBountyCount}/{project.totalBountyCount}
                                    </p>
                                    <div className="flex items-center gap-4 ">
                                        <AvatarImage w="w-8" h="h-8" url={project.creatorProfileImage} />
                                        <p className="text-textPrimary font-medium ">{project.creatorUsername}</p>
                                    </div>

                                    <Menus.Toggle id={project._id} />

                                    <Menus.List id={project._id}>
                                        <Menus.Button
                                            icon={<UpdateIcon />}
                                            shouldCloseMenu={true}
                                            onClick={(fn) => {
                                                handleOpenModal('update', project);
                                                fn();
                                            }}
                                        >
                                            Update
                                        </Menus.Button>
                                        {project.totalBountyCount === 0 && (
                                            <Menus.Button
                                                icon={<DeleteIcon />}
                                                shouldCloseMenu={true}
                                                onClick={(fn) => {
                                                    handleOpenModal('delete', project);
                                                    fn();
                                                }}
                                            >
                                                Delete
                                            </Menus.Button>
                                        )}
                                    </Menus.List>
                                </Table.Row>
                            ))
                        ) : (
                            <NoDataAvailable />
                        )
                    ) : (
                        <div className="min-h-[20vh] flex justify-center items-center">
                            <Loader />
                        </div>
                    )}
                    {totalEntries > 10 && (
                        <div className="w-[100%] mx-auto">
                            <Pagination
                                className="pt-6 pb-[.65rem]"
                                hoverColor="hover:text-textPrimary"
                                currentColor="bg-neutral50D"
                                textActive="text-textPrimary"
                                currentPage={currentPage}
                                totalPages={Math.ceil(totalEntries / entriesPerPage)}
                                entriesPerPage={entriesPerPage}
                                totalEntries={totalEntries}
                                entriesHandler={entriesHandler}
                                onPageChange={setCurrentPage}
                                border="border-borderUnderline"
                                hoverBorder="hover:border-borderUnderline"
                            />
                        </div>
                    )}
                </Table>
            </Menus>
            {isModalOpen && (
                <div>
                    {modalAction === 'update' && (
                        <CreateProjectModal
                            onUpdation={handleConfirmAction}
                            edit={true}
                            projectDetails={selectedProject}
                            close={handleCloseModal}
                        />
                    )}
                    {modalAction === 'delete' && (
                        <DeleteProjectModal
                            onDeletion={handleConfirmAction}
                            projectName={selectedProject.title}
                            projectId={selectedProject._id}
                            close={handleCloseModal}
                        />
                    )}
                </div>
            )}
        </>
    );
}

export default ProjectsList;
